import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import { openRazorpayCheckout, RAZORPAY_THEME_COLOR, getCheckoutImage, type RazorpayResponse } from '@/lib/razorpay';
import './Plans.css';

type Tier = 'free' | 'pro' | 'power' | 'enterprise';

interface Plan {
  id: string;
  name: string;
  price: string;
  currency: string;
  billingCycle: string;
  tier: Tier;
  tokenLimit: number | null;
  slotCap: number | null;
  monthlyCredits: number | null;
  dailyCreditCap: number | null;
  isDefault: boolean;
  visibility: string;
}

interface Subscription {
  id?: string;
  plan: Plan;
  [k: string]: unknown;
}

/** A coupon the user is eligible to SEE on this page (GET /coupons/offers). */
interface Offer {
  code: string;
  description: string | null;
  discountType: 'PERCENT' | 'FIXED';
  discountValue: string;
  maxDiscountAmount: string | null;
  currency: string;
  scope: 'GLOBAL' | 'NEW_USERS' | 'PER_USER';
  appliesToPlanIds: string[] | null;
  expiresAt: string | null;
}

/** Per-plan result of applying the entered code (GET /coupons/validate). */
interface CouponResult {
  finalAmount: number;
  discountAmount: number;
  originalAmount: number;
  currency: string;
}
type ValidateResp =
  | { valid: false; reason: string }
  | { valid: true; code: string; originalAmount: number; discountAmount: number; finalAmount: number; currency: string };

/** Small check glyph for the allowance line (matches the /welcome CheckoutCard). */
function Check() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Plans — user-facing plan selection / upgrade page.
 *
 * Pulls the public plan catalog (GET /api/v2/plans), the current subscription,
 * and the OFFERS this user is eligible to see (GET /api/v2/coupons/offers). A
 * code can be applied to preview the discounted price per plan
 * (GET /api/v2/coupons/validate); it's then passed to /api/v2/payments/initiate,
 * which recomputes the discount server-side.
 *
 * Styled as a calm cream/gold "statement" (mirrors the /welcome CheckoutCard):
 * a serif title, ONE hairline strip for coupon + offers, and a card per plan
 * with a serif price hero. Credits (not tokens) are the user-facing allowance;
 * there is deliberately no rolling usage meter here.
 */
export function Plans() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [selecting, setSelecting] = useState<string | null>(null);

  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  const [couponResults, setCouponResults] = useState<Record<string, CouponResult>>({});
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponBusy, setCouponBusy] = useState(false);
  // Deep-link from the signup handoff (?plan=&coupon=): auto-apply once + highlight.
  const [deepLinked, setDeepLinked] = useState(false);
  const [highlightName, setHighlightName] = useState<string | null>(null);

  async function loadSubscription(): Promise<void> {
    const me = await api.get<{ subscription: Subscription | null }>('/api/v2/subscriptions/me');
    setSubscription(me.subscription);
  }

  async function loadAll(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const plansRes = await api.get<{ plans: Plan[] }>('/api/v2/plans');
      setPlans(plansRes.plans ?? []);
      await loadSubscription();
      // Offers are best-effort — never block the page if they fail to load.
      try {
        const off = await api.get<{ offers: Offer[] }>('/api/v2/coupons/offers');
        setOffers(off.offers ?? []);
      } catch { /* ignore */ }
    } catch (e) {
      setError((e as Error).message || 'Could not load plans');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
  }, []);

  // Once plans are loaded, honor the signup deep-link: auto-apply the carried
  // coupon (so discounted prices show) and scroll the chosen plan into view. The
  // user still clicks Select — we never auto-open checkout.
  useEffect(() => {
    if (loading || deepLinked || plans.length === 0) return;
    setDeepLinked(true);
    const q = new URLSearchParams(window.location.search);
    const coupon = q.get('coupon');
    if (coupon) void applyCoupon(coupon);
    const planName = q.get('plan');
    if (planName) {
      setHighlightName(planName);
      setTimeout(() => {
        document
          .querySelector(`[data-plan-name="${planName.replace(/["\\]/g, '')}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 120);
    }
  }, [loading, plans, deepLinked]);

  /** Validate a code against every paid plan and remember where it applies, so
   *  each eligible plan card can show its discounted price up front. */
  async function applyCoupon(rawCode: string): Promise<void> {
    const code = rawCode.trim();
    if (!code) return;
    setCouponBusy(true);
    setCouponError(null);
    try {
      const paidPlans = plans.filter((p) => Number(p.price) > 0);
      const results: Record<string, CouponResult> = {};
      await Promise.all(
        paidPlans.map(async (p) => {
          try {
            const r = await api.get<ValidateResp>(
              `/api/v2/coupons/validate?code=${encodeURIComponent(code)}&planId=${encodeURIComponent(p.id)}`,
            );
            if (r.valid) {
              results[p.id] = {
                finalAmount: r.finalAmount,
                discountAmount: r.discountAmount,
                originalAmount: r.originalAmount,
                currency: r.currency,
              };
            }
          } catch { /* per-plan failure is non-fatal */ }
        }),
      );
      if (Object.keys(results).length === 0) {
        setCouponError("This code isn't valid for any plan you can buy right now.");
        setAppliedCode(null);
        setCouponResults({});
      } else {
        setAppliedCode(code.toUpperCase());
        setCouponResults(results);
        setCouponInput(code.toUpperCase());
        setFlash(null);
      }
    } finally {
      setCouponBusy(false);
    }
  }

  function clearCoupon(): void {
    setAppliedCode(null);
    setCouponResults({});
    setCouponError(null);
    setCouponInput('');
  }

  /** Finalize a paid order: verify the Checkout handshake on the backend and,
   *  on PAID, refresh the plan view. The authoritative webhook converges too. */
  async function finalizePayment(plan: Plan, resp: RazorpayResponse): Promise<void> {
    setFlash('Confirming your payment…');
    try {
      const r = await api.post<{ status: 'PENDING' | 'PAID' | 'FAILED' }>('/api/v2/payments/callback', {
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
      });
      if (r.status === 'FAILED') {
        setError('Payment was not completed. You can try again.');
        setSelecting(null);
        return;
      }
      await loadSubscription();
      clearCoupon();
      setFlash(r.status === 'PAID' ? `You're now on the ${plan.name} plan.` : 'Your payment is processing — it will reflect shortly.');
    } catch {
      setError('Could not confirm your payment. If you were charged, it will reflect shortly.');
    } finally {
      setSelecting(null);
    }
  }

  async function select(plan: Plan): Promise<void> {
    setSelecting(plan.id);
    setFlash(null);
    setError(null);
    try {
      const isFree = plan.price === '0' || Number(plan.price) === 0;
      if (isFree) {
        await api.post('/api/v2/subscriptions/select', { planId: plan.id });
        await loadSubscription();
        setFlash(`You're now on the ${plan.name} plan.`);
        setSelecting(null);
        return;
      }
      const existingPlanId = subscription?.plan?.id ?? null;
      const upgrade = existingPlanId != null && existingPlanId !== plan.id;
      // Only pass the code if it validated for THIS plan.
      const useCoupon = appliedCode && couponResults[plan.id] ? appliedCode : undefined;
      const order = await api.post<{ orderId: string; keyId: string; amount: number; currency: string; activated?: boolean }>(
        `/api/v2/payments/initiate${upgrade ? '?upgrade=true' : ''}`,
        { planId: plan.id, ...(useCoupon ? { couponCode: useCoupon } : {}) },
      );
      // 100%-off / below-minimum coupon: the server activated directly — no Razorpay.
      if (order.activated) {
        await loadSubscription();
        clearCoupon();
        setFlash(`You're now on the ${plan.name} plan — your coupon covered it in full.`);
        setSelecting(null);
        return;
      }
      let handled = false;
      await openRazorpayCheckout({
        key: order.keyId,
        order_id: order.orderId,
        amount: order.amount,
        currency: order.currency,
        name: 'BREWW AI',
        description: 'Product of Datacaffe.ai',
        image: getCheckoutImage(),
        theme: { color: RAZORPAY_THEME_COLOR },
        handler: (resp) => { handled = true; void finalizePayment(plan, resp); },
        modal: {
          ondismiss: () => {
            if (handled) return;
            setSelecting(null);
            setFlash('Checkout was closed before payment completed.');
          },
        },
      });
    } catch (e) {
      setError((e as Error).message || 'Could not start checkout');
      setSelecting(null);
    }
  }

  const currentPlanId = subscription?.plan?.id ?? null;

  /** Currency prefix — ₹ for INR, else the code + a space (matches CheckoutCard). */
  function cur(currency: string): string {
    return currency === 'INR' ? '₹' : `${currency} `;
  }
  /** Trim the trailing ".00" and group thousands (Indian grouping). */
  function fmt(n: number | string): string {
    return Number(n).toLocaleString('en-IN');
  }

  // Credits are the user-facing allowance; the internal token count is never
  // surfaced here (real plans all carry a daily credit cap).
  function allowanceLabel(plan: Plan): string {
    if (plan.dailyCreditCap != null) return `up to ${plan.dailyCreditCap.toLocaleString()} credits / day`;
    return 'Unlimited';
  }

  function offerLabel(o: Offer): string {
    return o.discountType === 'PERCENT'
      ? `${Number(o.discountValue)}% off`
      : `${cur(o.currency)}${fmt(o.discountValue)} off`;
  }

  return (
    <div className="plans-page">
      <header className="plans-topbar">
        <h1>Choose your plan</h1>
        <a className="plans-back" href="/chat">← Back to chat</a>
      </header>

      <div className="plans-container">
        {flash && <div className="plans-flash">{flash}</div>}
        {error && <div className="plans-error">{error}</div>}

        {loading && !error && <p className="plans-empty">Loading…</p>}

        {!loading && !error && (
          <>
            {/* One hairline strip: coupon entry (left) + eligible offers as chips (right). */}
            <div className="plans-bar">
              <div className="plans-bar__coupon">
                <label className="plans-bar__label" htmlFor="coupon">Coupon code</label>
                <div className="pcoupon">
                  <input
                    id="coupon"
                    className="pcoupon__field"
                    type="text"
                    placeholder="Enter code"
                    value={couponInput}
                    disabled={couponBusy}
                    onChange={(e) => setCouponInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void applyCoupon(couponInput); }}
                  />
                  {appliedCode ? (
                    <button type="button" className="pcoupon__clear" onClick={clearCoupon}>Remove</button>
                  ) : (
                    <button
                      type="button"
                      className="pcoupon__apply"
                      disabled={couponBusy || !couponInput.trim()}
                      onClick={() => void applyCoupon(couponInput)}
                    >
                      {couponBusy ? 'Checking…' : 'Apply'}
                    </button>
                  )}
                </div>
                {couponError && <div className="pcoupon__err">{couponError}</div>}
                {appliedCode && !couponError && (
                  <div className="pcoupon__ok"><span className="pcoupon__chip">✓ {appliedCode} applied</span></div>
                )}
              </div>

              {offers.length > 0 && (
                <div className="plans-bar__offers">
                  {offers.map((o) => (
                    <button
                      type="button"
                      key={o.code}
                      className={`offer-chip${appliedCode === o.code ? ' is-applied' : ''}`}
                      disabled={couponBusy || appliedCode === o.code}
                      onClick={() => void applyCoupon(o.code)}
                      title={o.description || undefined}
                    >
                      {offerLabel(o)} · {o.code}{appliedCode === o.code ? ' ✓' : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="plans-grid">
              {plans.map((plan) => {
                const isCurrent = plan.id === currentPlanId;
                const busy = selecting === plan.id;
                const isFree = plan.price === '0' || Number(plan.price) === 0;
                const discounted = appliedCode ? couponResults[plan.id] : undefined;
                const c = cur(plan.currency);
                const cycle = plan.billingCycle.toLowerCase();
                return (
                  <div
                    className={`plan-card${isCurrent ? ' is-current' : ''}${discounted ? ' has-offer' : ''}`}
                    key={plan.id}
                    data-plan-name={plan.name}
                    style={highlightName === plan.name ? { outline: '2px solid var(--color-warning)', outlineOffset: 2 } : undefined}
                  >
                    <div className="plan-card__head">
                      <span className="plan-card__name">{plan.name}</span>
                      <span className={`plans-tier-badge tier-${plan.tier}`}>{plan.tier}</span>
                    </div>

                    <div className="plan-card__price">
                      {isFree ? (
                        <span className="plan-card__amt">Free</span>
                      ) : discounted ? (
                        <>
                          <s className="plan-card__was">{c}{fmt(plan.price)}</s>
                          <span className="plan-card__now">{c}{fmt(discounted.finalAmount)}</span>
                          <span className="plan-card__per"> / {cycle}</span>
                        </>
                      ) : (
                        <>
                          <span className="plan-card__amt">{c}{fmt(plan.price)}</span>
                          <span className="plan-card__per"> / {cycle}</span>
                        </>
                      )}
                    </div>

                    {discounted && (
                      <div className="plan-card__save">You save {c}{fmt(discounted.discountAmount)} with {appliedCode}</div>
                    )}

                    <div className="plan-card__allowance"><Check /> {allowanceLabel(plan)}</div>
                    {plan.slotCap != null && (
                      <div className="plan-card__meta">{plan.slotCap.toLocaleString()} connector slots</div>
                    )}

                    <div className="plan-card__actions">
                      {isCurrent ? (
                        <span className="plan-card__badge">Current plan</span>
                      ) : (
                        <button
                          type="button"
                          className="plan-card__cta"
                          disabled={busy || selecting != null}
                          onClick={() => void select(plan)}
                        >
                          {busy ? 'Switching…' : discounted ? 'Select with offer' : 'Select plan'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {plans.length === 0 && <p className="plans-empty">No plans are available right now.</p>}

            <p className="plans-trust">
              Secure checkout <i>·</i> Cancel anytime <i>·</i> Free stays fully usable
            </p>
          </>
        )}
      </div>
    </div>
  );
}
